import { useQuery } from '@tanstack/react-query';
import type { ShiftRow } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportPeriodButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { Badge } from '@/components/Badge';
import { fetchShifts, reportKeys } from '@/features/reports/api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);

/** A closed till whose counted cash doesn't match the expected float is worth a glance, not just a number. */
const CASH_MISMATCH_TOLERANCE = 0.01;

function hasCashMismatch(row: ShiftRow): boolean {
  return Math.abs(row.expectedCash - row.actualCash) > CASH_MISMATCH_TOLERANCE;
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

const FILTER_FIELDS = ['Opened by', 'Status'] as const;

const TOGGLEABLE = [
  'closeTime', 'openedBy', 'openingCash', 'cashSales', 'cardSales', 'creditSales',
  'totalSales', 'totalOrders', 'taxCollected', 'returnsAmount', 'expectedCash',
  'actualCash', 'status',
] as const;

const COLUMN_LABELS: Record<string, string> = {
  closeTime: 'Closed',
  openedBy: 'Opened by',
  openingCash: 'Opening cash',
  cashSales: 'Cash sales',
  cardSales: 'Card sales',
  creditSales: 'Credit sales',
  totalSales: 'Total sales',
  totalOrders: 'Orders',
  taxCollected: 'Tax collected',
  returnsAmount: 'Returns',
  expectedCash: 'Expected cash',
  actualCash: 'Actual cash',
  status: 'Status',
};

const SHIFT_COLUMNS: Column<ShiftRow>[] = [
  { key: 'openTime', header: 'Opened', pinned: true, cell: (row) => formatDateTime(row.openTime) },
  { key: 'closeTime', header: 'Closed', cell: (row) => formatDateTime(row.closeTime) },
  { key: 'openedBy', header: 'Opened by', cell: (row) => row.openedBy },
  { key: 'openingCash', header: 'Opening cash', align: 'right', cell: (row) => money(row.openingCash) },
  { key: 'cashSales', header: 'Cash sales', align: 'right', cell: (row) => money(row.cashSales) },
  { key: 'cardSales', header: 'Card sales', align: 'right', cell: (row) => money(row.cardSales) },
  { key: 'creditSales', header: 'Credit sales', align: 'right', cell: (row) => money(row.creditSales) },
  { key: 'totalSales', header: 'Total sales', align: 'right', cell: (row) => money(row.totalSales) },
  { key: 'totalOrders', header: 'Orders', align: 'right', cell: (row) => String(row.totalOrders) },
  { key: 'taxCollected', header: 'Tax collected', align: 'right', cell: (row) => money(row.taxCollected) },
  { key: 'returnsAmount', header: 'Returns', align: 'right', cell: (row) => money(row.returnsAmount) },
  {
    key: 'expectedCash',
    header: 'Expected cash',
    align: 'right',
    cell: (row) => money(row.expectedCash),
  },
  {
    key: 'actualCash',
    header: 'Actual cash',
    align: 'right',
    cell: (row) => (
      <span className="inline-flex items-center gap-1.5">
        {money(row.actualCash)}
        {hasCashMismatch(row) ? (
          <Badge tone="warning" dot>
            Mismatch
          </Badge>
        ) : null}
      </span>
    ),
  },
  { key: 'status', header: 'Status', cell: (row) => row.status },
];

export function ShiftReportsPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const toolbar = useReportToolbar(filters, setFilters);
  const layout = useReportLayout('shift-reports', TOGGLEABLE);
  const columns = applyReportLayout(SHIFT_COLUMNS, layout);

  const query = useQuery({
    queryKey: reportKeys.shifts(filters),
    queryFn: ({ signal }) => fetchShifts(filters, signal),
  });

  return (
    <div>
      <PageHeader title="Shift reports" description="One row per till session, dated by its open time." />
      <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} hideDateRange />
      <ReportToolbar>
        <ReportSearchField
          value={toolbar.search}
          onChange={toolbar.setSearch}
          placeholder="Search staff or status"
        />
        <ReportFiltersButton
          fields={FILTER_FIELDS}
          applied={toolbar.filterRows}
          matchAny={toolbar.matchAny}
          onApply={toolbar.applyFilters}
          placeholderFor={(field) => (field === 'Status' ? 'Open / Closed' : 'name')}
        />
        <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
        <ReportPeriodButton
          from={filters.from}
          to={filters.to}
          onChange={toolbar.setRange}
          deselected={toolbar.filterActive}
        />
      </ReportToolbar>
      <AsyncSection
        state={query}
        // This endpoint returns its whole result set, so the toolbar's
        // predicate is applied here rather than in the query (see
        // applyClientFilters) — equivalent, and nothing is paginated away.
        select={(rows: ShiftRow[]) =>
          applyClientFilters(rows, {
            search: toolbar.search,
            filterRows: toolbar.filterRows,
            matchAny: toolbar.matchAny,
            fieldValue: (row, field) =>
              field === 'Opened by' ? row.openedBy : field === 'Status' ? row.status : undefined,
            searchValues: (row) => [row.openedBy, row.status],
          })
        }
        isEmpty={(rows) => rows.length === 0}
        emptyTitle="No shifts in this window"
        skeleton={<TableSkeleton columns={13} />}
      >
        {(rows) => (
          <DataTable
            caption="Shift reports"
            layout={layout}
            columns={columns}
            rows={rows}
            rowKey={(row, index) => `${row.openTime}-${index}`}
            rowClassName={(row) => (hasCashMismatch(row) ? 'bg-amber-50' : undefined)}
          />
        )}
      </AsyncSection>
    </div>
  );
}
